import React, { FC } from "react";
import { BootResult } from "shared-ts-ui-app-boot";
import type { RouteById, RouterIds } from "firefly-my-chimney-ts-router";
import { router } from "firefly-my-chimney-ts-router";
import { type LazyRoute, RouterProvider } from "@tanstack/react-router";
import { RootComponent } from "./RootComponent";
import { setMuiLicenseKey } from "shared-ts-mui";

setMuiLicenseKey();

// Generic to enforce that the route returned matches the route path
type LazyRouteFn<TRoutePath extends RouterIds> = () => Promise<
  LazyRoute<RouteById<(typeof router)["routeTree"], TRoutePath>>
>;

type RouterMap = {
  // __root__ is a special route that isn't lazy loaded, so we need to manually bind it
  // You could consider adding null to the returned type to have routes without rendering
  [K in Exclude<RouterIds, "__root__">]: LazyRouteFn<K>;
};

const routerMap: RouterMap = {
  "/_myChimneyContext": () =>
    import("my-chimney-ts-my-chimney-context").then(
      (d) => d.MyChimneyContextLoaderRoute
    ),
  "/_myChimneyContext/": () =>
    import("firefly-my-chimney-ts-key-figures").then(
      (d) => d.KeyFiguresPageRoute
    ),
  "/_myChimneyContext/summary": () =>
    import("mc-ts-summary").then((d) => d.SummaryPageRoute),
  "/_myChimneyContext/burns": () =>
    import("firefly-my-chimney-ts-pages").then((d) => d.BurnsPageRoute),
  "/_myChimneyContext/history": () =>
    import("firefly-my-chimney-ts-pages").then((d) => d.HistoryPageRoute),
  "/_myChimneyContext/combustion-plant": () =>
    import("firefly-my-chimney-ts-pages").then(
      (d) => d.CombustionPlantPageRoute
    ),
  "/guidance": () =>
    import("firefly-my-chimney-ts-pages").then((d) => d.GuidancePageRoute),
  "/user-feedback": () =>
    import("firefly-my-chimney-ts-pages").then((d) => d.UserFeedbackPageRoute),
  "/about": () =>
    import("firefly-my-chimney-ts-pages").then((d) => d.AboutPageRoute),
  "/user": () =>
    import("firefly-my-chimney-ts-pages").then((d) => d.UserPageRoute),
};

// Given __root__ is a special route that isn't lazy loaded, we need to update it manually
router.routesById["__root__"].update({
  component: RootComponent,
});

Object.entries(routerMap).forEach(([path, component]) => {
  const foundRoute = router.routesById[path as RouterIds];
  // Bind the lazy route to the actual route
  foundRoute.lazy(component as any);
});

export interface AppProps {
  bootResult: BootResult;
}

export const App: FC<AppProps> = (props) => {
  return <RouterProvider router={router} />;
};
